import { Box, Input, Select, Textarea, Tooltip } from '@chakra-ui/react';

// ─── FillHandle ───────────────────────────────────────────────────────────────

export function FillHandle({ onMouseDown }: { onMouseDown: (e: React.MouseEvent) => void }) {
  return (
    <Box
      className="fill-handle"
      position="absolute"
      bottom="-4px"
      right="-4px"
      w="8px"
      h="8px"
      bg="brand.500"
      border="1.5px solid white"
      borderRadius="1px"
      cursor="crosshair"
      zIndex={2}
      onMouseDown={onMouseDown}
    />
  );
}

// ─── TextCell ─────────────────────────────────────────────────────────────────

export function TextCell({
  id,
  testId,
  value,
  onChange,
  onKeyDown,
  error,
  onFillStart,
  placeholder,
  maxLength,
}: {
  id?: string;
  testId?: string;
  value: string;
  onChange: (v: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  error?: string;
  onFillStart?: () => void;
  placeholder?: string;
  maxLength?: number;
}) {
  return (
    <Box
      position="relative"
      sx={{ '& .fill-handle': { opacity: 0, transition: 'opacity 0.1s' }, '&:hover .fill-handle': { opacity: 1 } }}
    >
      <Tooltip label={error} isDisabled={!error} placement="top" hasArrow bg="red.500">
        <Input
          id={id}
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          maxLength={maxLength}
          size="sm"
          variant="unstyled"
          bg="transparent"
          px={2}
          py={1}
          h="32px"
          borderRadius="none"
          borderWidth="1px"
          borderColor={error ? 'red.400' : 'border-default'}
          _hover={{ borderColor: error ? 'red.500' : 'border-hover' }}
          _focus={{ borderColor: 'brand.500', outline: 'none' }}
        />
      </Tooltip>
      {onFillStart && (
        <FillHandle onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onFillStart(); }} />
      )}
    </Box>
  );
}

// ─── TextAreaCell ─────────────────────────────────────────────────────────────

export function TextAreaCell({
  id,
  testId,
  value,
  onChange,
  onKeyDown,
  error,
  onFillStart,
  placeholder,
}: {
  id?: string;
  testId?: string;
  value: string;
  onChange: (v: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  error?: string;
  onFillStart?: () => void;
  placeholder?: string;
}) {
  return (
    <Box
      position="relative"
      sx={{ '& .fill-handle': { opacity: 0, transition: 'opacity 0.1s' }, '&:hover .fill-handle': { opacity: 1 } }}
    >
      <Tooltip label={error} isDisabled={!error} placement="top" hasArrow bg="red.500">
        <Textarea
          id={id}
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            // Let Enter insert a newline instead of triggering row navigation.
            if (e.key === 'Enter') return;
            onKeyDown?.(e);
          }}
          placeholder={placeholder}
          size="sm"
          variant="unstyled"
          bg="transparent"
          px={2}
          py={1}
          rows={2}
          resize="vertical"
          borderRadius="none"
          borderWidth="1px"
          borderColor={error ? 'red.400' : 'border-default'}
          _hover={{ borderColor: error ? 'red.500' : 'border-hover' }}
          _focus={{ borderColor: 'brand.500', outline: 'none' }}
        />
      </Tooltip>
      {onFillStart && (
        <FillHandle onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onFillStart(); }} />
      )}
    </Box>
  );
}

// ─── SelectCell ───────────────────────────────────────────────────────────────

export function SelectCell({
  id,
  testId,
  value,
  onChange,
  onKeyDown,
  options,
  error,
  onFillStart,
}: {
  id?: string;
  testId?: string;
  value: string;
  onChange: (v: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  options: { value: string; label: string }[];
  error?: string;
  onFillStart?: () => void;
}) {
  return (
    <Box
      position="relative"
      sx={{ '& .fill-handle': { opacity: 0, transition: 'opacity 0.1s' }, '&:hover .fill-handle': { opacity: 1 } }}
    >
      <Tooltip label={error} isDisabled={!error} placement="top" hasArrow bg="red.500">
        <Select
          id={id}
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          size="sm"
          variant="unstyled"
          bg="transparent"
          px={2}
          h="32px"
          borderRadius="none"
          borderWidth="1px"
          borderColor={error ? 'red.400' : 'border-default'}
          _hover={{ borderColor: error ? 'red.500' : 'border-hover' }}
          _focus={{ borderColor: 'brand.500', outline: 'none' }}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </Select>
      </Tooltip>
      {onFillStart && (
        <FillHandle onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onFillStart(); }} />
      )}
    </Box>
  );
}
